import React, { useContext, useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { GridColumnMenuSort, GridColumnMenuColumnsList } from '@progress/kendo-react-grid';
import ColumnMenuContext from './ColumnMenuContext';

function ColumnMenu(props) {
  const { columnsState, columns, onColumnsChange } = useContext(ColumnMenuContext);
  const { onCloseMenu = () => {} } = props;
  const menuRef = useRef(null);
  const menuStyle = {
    maxHeight: 'calc(100vh - 8rem)',
    overflowX: 'hidden',
    overflowY: 'auto'
  };

  useLayoutEffect(() => {
    const menuElement = menuRef.current;
    if (!menuElement) return;
    const animationContainer = menuElement.closest('.k-animation-container');
    if (!animationContainer) return;
    const actionsContainer = menuElement.querySelector('.k-actions');
    if (actionsContainer) {
      actionsContainer.style.position = 'sticky';
      actionsContainer.style.bottom = '0';
      actionsContainer.style.zIndex = '1';
      actionsContainer.style.background = '#fff';
      actionsContainer.style.paddingTop = '8px';
      actionsContainer.style.paddingBottom = '4px';
    }

    // Mirrors the adjustColumnMenuPosition pattern in gridSettings.js:
    // only shift the animation container's top to keep the popup inside the viewport.
    // maxHeight is intentionally NOT modified here — the CSS calc(100vh - 8rem) already
    // caps the menu height, so the only thing left to do is reposition when the popup
    // would bleed off the bottom (or top) of the screen.
    const adjustMenuVerticalPosition = () => {
      const viewportPadding = 8;
      const rect = menuElement.getBoundingClientRect();
      const overflowBottom = rect.bottom - window.innerHeight + viewportPadding;
      const overflowTop = viewportPadding - rect.top;

      if (overflowBottom > 0 || overflowTop > 0) {
        const rawTop = animationContainer.style.top || window.getComputedStyle(animationContainer).top || '0';
        const currentTop = Number.parseFloat(rawTop) || 0;
        const adjustedTop = Math.max(
          viewportPadding,
          currentTop - Math.max(overflowBottom, 0) + Math.max(overflowTop, 0)
        );
        animationContainer.style.top = `${adjustedTop}px`;
      }
    };

    // Wait for Kendo's open animation (translateY transition) to finish before correcting
    // the popup position. The inner .k-child-animation-container owns the transform, so
    // transitionend fires on it when the animation settles — at that point getBCR() is
    // accurate. A 400 ms fallback handles menus that open without animation.
    const childAnimCont = animationContainer.querySelector('.k-child-animation-container') || animationContainer;

    const onTransitionEnd = (e) => {
      if (e.target !== childAnimCont || e.propertyName !== 'transform') return;
      childAnimCont.removeEventListener('transitionend', onTransitionEnd);
      window.clearTimeout(fallbackTimeoutId);
      adjustMenuVerticalPosition();
    };

    childAnimCont.addEventListener('transitionend', onTransitionEnd);

    const fallbackTimeoutId = window.setTimeout(() => {
      childAnimCont.removeEventListener('transitionend', onTransitionEnd);
      adjustMenuVerticalPosition();
    }, 400);

    return () => {
      childAnimCont.removeEventListener('transitionend', onTransitionEnd);
      window.clearTimeout(fallbackTimeoutId);
      animationContainer.style.removeProperty('top');
      if (actionsContainer) {
        actionsContainer.style.removeProperty('position');
        actionsContainer.style.removeProperty('bottom');
        actionsContainer.style.removeProperty('z-index');
        actionsContainer.style.removeProperty('background');
        actionsContainer.style.removeProperty('padding-top');
        actionsContainer.style.removeProperty('padding-bottom');
      }
    };
  }, []);

  return (
    <div className="ip-kendo-column-menu" style={menuStyle} ref={menuRef}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading -- Kendo column menu sort expects full Grid column menu props */}
      {props.sortable && <GridColumnMenuSort {...props} />}
      <GridColumnMenuColumnsList
        columnsState={columnsState}
        columns={columns}
        onColumnsChange={onColumnsChange}
        onCloseMenu={onCloseMenu}
      />
    </div>
  );
}

ColumnMenu.propTypes = {
  sortable: PropTypes.bool.isRequired,
  onCloseMenu: PropTypes.func.isRequired
};

export default ColumnMenu;
